'use client';

import { useState } from 'react';
import { CalendarClock, Check, Users } from 'lucide-react';

import { Badge } from 'lib/components/ui/badge';
import { Separator } from 'lib/components/ui/separator';
import { type EventParticipant } from 'modules/App/models/EventParticipant';
import { cn } from 'lib/utils';
import Markdown from 'lib/components/ui/markdown';
import { formatMinutesToHoursAndMinutes } from 'utils/misc';
import { MdLock } from 'react-icons/md';
import { useAuthModal } from 'modules/auth';
interface EventCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  date: string;
  time: string;
  author: {
    name: string;
    url?: string;
    avatar?: string;
  };
  description: string;
  duration: number;
  participants: number;
  maxParticipants: number;
  initialStatus?: EventParticipant['status'];
  children?: React.ReactNode;
  onClose?: () => void;
  isFinished?: boolean;
  onStatusChange?: (status: EventParticipant['status']) => void;
  readOnly?: boolean;
}

export const EventCard: React.FC<EventCardProps> = ({
  title,
  date,
  time,
  author,
  participants: initialParticipants,
  maxParticipants,
  initialStatus = null,
  children,
  className,
  duration,
  onClose,
  description,
  onStatusChange,
  isFinished,
  readOnly,
  ...props
}) => {
  const { toggleModal } = useAuthModal();
  const [status, setStatus] = useState<EventParticipant['status'] | undefined>(initialStatus || undefined);
  const [participants, setParticipants] = useState(initialParticipants);
  const [loading, setLoading] = useState(false);
  const handleRsvp = async (newStatus: EventParticipant['status']) => {
    setLoading(true);
    await onStatusChange?.(newStatus);
    setStatus(newStatus);
    if (newStatus === 'registered') {
      setParticipants(participants + 1);
    } else {
      setParticipants(participants - 1);
    }
    setLoading(false);
  };

  const isAtCapacity = participants >= maxParticipants && maxParticipants > 0;

  return (
    <article className={cn('w-full relative flex flex-col', className)} {...props}>
      <header className="pb-3">
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-semibold">{title}</h2>
          {isAtCapacity && <Badge variant="destructive">COMPLET</Badge>}
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <CalendarClock className="h-4 w-4" />
          <span>
            {date} • {time}
          </span>
        </div>
      </header>
      <div className="py-3 flex-1">
        <div className="flex items-center gap-3 mb-0">
          <span className="text-sm font-medium w-20">Organisé par</span>
          <span className="text-sm text-muted-foreground">
            <a href={author.url} target="_blank" rel="noopener noreferrer" className="link">
              {author.name}
            </a>
          </span>
        </div>
        <div className="flex items-center gap-3 mb-4">
          <span className="text-sm font-medium w-20">Durée</span>
          <span className="text-sm text-muted-foreground">{formatMinutesToHoursAndMinutes(duration)}</span>
        </div>
        {participants > 0 ? (
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              <span className="font-medium">{participants}</span>
              {maxParticipants > 0 ? (
                <>
                  <span className="text-muted-foreground"> sur </span>
                  <span className="font-medium">{maxParticipants}</span>
                  <span className="text-muted-foreground"> participants</span>
                </>
              ) : (
                <span className="text-muted-foreground"> participant{participants > 1 ? 's' : ''}</span>
              )}
            </span>
          </div>
        ) : maxParticipants > 0 ? (
          <span className="text-muted-foreground">
            <span className="font-medium">{participants}</span> places
          </span>
        ) : null}
        <div className="prose prose-sm mt-5 border-t border-b border-gray-200 py-2">
          <Markdown>{description || ''}</Markdown>
        </div>
        {children}
      </div>
      <Separator />

      <footer className="">
        {readOnly ? (
          <button type="button" className="flex-1 btn btn-sm btn-primary mx-auto" onClick={toggleModal}>
            <MdLock className="mr-1 h-4 w-4 text-muted-foreground" />
            Accès réservé aux membres
          </button>
        ) : (
          <div className="flex w-full gap-2">
            {isFinished ? (
              <button type="button" className="flex-1 btn btn-sm btn-primary" disabled>
                Désolé, cet évènement est terminé
              </button>
            ) : isAtCapacity && status !== 'registered' ? (
              <button type="button" className="flex-1 btn btn-sm btn-primary" disabled>
                Désolé, plus de place disponible
              </button>
            ) : status === 'registered' ? (
              <button type="button" className="flex-1 btn btn-sm btn-danger" onClick={() => handleRsvp('canceled')}>
                {loading && <span className="loading"></span>}
                J’annule
              </button>
            ) : status === 'canceled' || !status ? (
              <button type="button" className={'flex-1 btn btn-sm btn-primary'} onClick={() => handleRsvp('registered')} disabled={isAtCapacity}>
                {loading ? <span className="loading"></span> : <Check className={cn('mr-1 h-4 w-4 text-muted-foreground')} />}
                Je veux y participer
              </button>
            ) : (
              <button type="button" className={'flex-1 btn btn-sm btn-primary'} onClick={() => handleRsvp('registered')} disabled={isAtCapacity}>
                {loading ? <span className="loading"></span> : <Check className={cn('mr-1 h-4 w-4 text-muted-foreground')} />}
                Je veux y participer
              </button>
            )}
          </div>
        )}
      </footer>
    </article>
  );
};
